import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { getOrderStatus, type OrderStatus } from '../../lib/catalog';
import { ApiError } from '../../lib/apiClient';
import { recallOrderToken } from '../../lib/orderTokens';
import { formatDate, formatMoney, formatTime, shortId } from '../../lib/format';
import { TicketQr } from '../../components/TicketQr';
import { useCopy } from '../../copy/CopyProvider';
import './OrderConfirmationPage.css';

/**
 * Payment authorization and ticket issuance are deliberately separate in the
 * engine: Stripe confirms the card, and only the signed
 * payment_intent.succeeded webhook activates the tickets and generates QR codes.
 * That gap is normally a second or two but is not instant and is not
 * guaranteed, so this screen polls rather than assuming.
 *
 * Backoff keeps a stuck webhook from turning into a hammering loop, and after
 * the ceiling we stop and tell the buyer the truth: payment succeeded, tickets
 * are following by email.
 */
const POLL_INTERVAL_MS = 2000;
const MAX_POLL_MS = 90_000;

export function OrderConfirmationPage() {
  const { orderId } = useParams<{ orderId: string }>();
  const t = useCopy();

  const [order, setOrder] = useState<OrderStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [timedOut, setTimedOut] = useState(false);

  useEffect(() => {
    if (!orderId) return;

    // One self-cancelling loop owned by this effect, rather than a callback that
    // re-schedules itself: unmounting flips `active` and the loop simply stops.
    let active = true;
    let timer: number | undefined;
    const startedAt = Date.now();

    const wait = (ms: number) =>
      new Promise<void>((resolve) => {
        timer = window.setTimeout(resolve, ms);
      });

    (async () => {
      while (active) {
        try {
          const token = recallOrderToken(orderId);
          const result = await getOrderStatus(orderId, token);
          if (!active) return;

          setOrder(result);
          setError(null);
          setLoading(false);

          const ready =
            result.status === 'complete' && result.tickets.every((t) => t.qr != null);
          if (ready) return;

          if (Date.now() - startedAt > MAX_POLL_MS) {
            setTimedOut(true);
            return;
          }
        } catch (err: unknown) {
          if (!active) return;
          // A 404 means neither a token nor ownership authorized this read.
          setError(
            err instanceof ApiError && err.status === 404
              ? 'We could not find that order. If you just paid, check your email for your tickets.'
              : 'Could not load your order.',
          );
          setLoading(false);
          return;
        }

        await wait(POLL_INTERVAL_MS);
      }
    })();

    return () => {
      active = false;
      if (timer != null) window.clearTimeout(timer);
    };
  }, [orderId]);

  if (loading) return <p className="confirm__status">Loading your order…</p>;

  if (error) {
    return (
      <div className="confirm__status confirm__status--error">
        <p role="alert">{error}</p>
        <Link to="/events" className="button button--secondary">
          Browse events
        </Link>
      </div>
    );
  }

  if (!order) return null;

  const paid = order.status === 'complete';
  const ticketsReady = paid && order.tickets.every((t) => t.qr != null);
  const time = formatTime(order.event.time);

  return (
    <div className="confirm">
      <header className={`confirm__header${paid ? ' confirm__header--paid' : ''}`}>
        <span className="confirm__icon" aria-hidden="true">
          {paid ? '✓' : '…'}
        </span>
        <h1 className="confirm__title display">
          {paid ? "You're going" : 'Confirming your payment'}
        </h1>
        <p className="confirm__lede">
          {paid
            ? `Your tickets for ${order.event.title} are confirmed.`
            : 'Your payment is being confirmed. This usually takes a few seconds.'}
        </p>
        {order.buyerEmail && paid && (
          <p className="confirm__email">
            A copy has been emailed to <span className="mono">{order.buyerEmail}</span>
          </p>
        )}
      </header>

      <section className="confirm__event card">
        <h2 className="confirm__event-title">{order.event.title}</h2>
        <p className="confirm__event-meta">
          {formatDate(order.event.date)}
          {time ? ` · ${time}` : ''}
        </p>
        {order.event.venue && <p className="confirm__event-meta">{order.event.venue}</p>}

        <dl className="confirm__order-meta">
          <div>
            <dt>Order</dt>
            <dd className="mono" title={order.orderId}>
              {shortId(order.orderId)}
            </dd>
          </div>
          <div>
            <dt>Total paid</dt>
            <dd className="mono">{formatMoney(order.totalCents)}</dd>
          </div>
          <div>
            <dt>Tickets</dt>
            <dd className="mono">{order.tickets.length}</dd>
          </div>
        </dl>
      </section>

      <section className="confirm__tickets">
        <h2 className="confirm__section-title">
          Your tickets
          {!ticketsReady && <span className="confirm__pending-tag">Generating…</span>}
        </h2>

        {!ticketsReady && !timedOut && (
          <p className="confirm__waiting">
            <span className="confirm__spinner" aria-hidden="true" />
            Waiting for payment confirmation from your bank. Your QR codes appear here
            automatically, no need to refresh.
          </p>
        )}

        {timedOut && !ticketsReady && (
          <p className="confirm__waiting confirm__waiting--slow" role="status">
            This is taking longer than usual. Your payment went through and your tickets will
            be emailed to you shortly. You can safely close this page.
          </p>
        )}

        <div className="confirm__ticket-grid">
          {order.tickets.map((ticket, index) => (
            <TicketQr key={ticket.ticketId} ticket={ticket} index={index + 1} />
          ))}
        </div>
      </section>

      <footer className="confirm__footer">
        {/* The primary action: the confirmation page reports the purchase, the ticket
            page is where the tickets live. */}
        <Link to={`/orders/${orderId}/tickets`} className="button button--primary">
          {t('order.confirmation.cta.viewTickets', 'View tickets')}
        </Link>
        <Link to="/events" className="button button--ghost">
          Browse more events
        </Link>
        <Link to="/account/orders" className="button button--secondary">
          My orders
        </Link>
      </footer>
    </div>
  );
}
